import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Star, ShoppingCart, Users, Clock, ArrowLeft, Minus, Plus, BookOpen, Bell } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useCart } from '../contexts/CartContext';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { getLocalizedField, pickLocalizedText } from '../utils/localizedContent';


export default function ProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [reviewLoading, setReviewLoading] = useState(false);
  const [added, setAdded] = useState(false);
  const [reserving, setReserving] = useState(false);
  const [reserveMsg, setReserveMsg] = useState('');
  const [notified, setNotified] = useState(false);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const { addItem } = useCart();
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const text = (es, en) => pickLocalizedText(lang, es, en);

  useEffect(() => {
    axios.get(`${API}/products/${productId}`).then(r => setProduct(r.data)).catch(() => navigate('/store'));
  }, [productId, navigate]);

  useEffect(() => {
    if (location.hash !== '#product-review' || !product) return;
    const el = document.getElementById('product-review');
    if (el) {
      window.requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [location.hash, product]);

  useEffect(() => {
    if (product && user) {
      axios.get(`${API}/products/${product.product_id}/notify-me`)
        .then(r => setNotified(r.data?.subscribed || false)).catch(() => {});
    }
  }, [product, user]);

  const handleToggleNotify = async () => {
    if (!user) { navigate('/auth'); return; }
    setNotifyLoading(true);
    try {
      if (notified) {
        await axios.delete(`${API}/products/${product.product_id}/notify-me`);
        setNotified(false);
      } else {
        await axios.post(`${API}/products/${product.product_id}/notify-me`);
        setNotified(true);
      }
    } catch (err) { console.error(err); }
    setNotifyLoading(false);
  };

  const handleAddCart = () => {
    addItem(product, quantity);
    setAdded(true);
    setTimeout(() => setAdded(false), 2000);
  };

  const handlePreOrder = async () => {
    if (!user) { navigate('/auth'); return; }
    setReserving(true);
    setReserveMsg('');
    try {
      await axios.post(`${API}/product-reservations`, { product_id: product.product_id, quantity: 1 });
      setReserveMsg(text('¡Reserva realizada! Te avisaremos cuando esté disponible.', 'Pre-order placed! We\'ll notify you when available.'));
    } catch (err) {
      setReserveMsg(err.response?.data?.detail || text('Error al reservar', 'Error placing reservation'));
    }
    setReserving(false);
  };

  const handleReview = async (e) => {
    e.preventDefault();
    if (!user) { navigate('/auth'); return; }
    setReviewLoading(true);
    try {
      await axios.post(`${API}/products/${productId}/review`, reviewForm);
      const { data } = await axios.get(`${API}/products/${productId}`);
      setProduct(data);
      setReviewForm({ rating: 5, comment: '' });
    } catch (err) {
      console.error('Failed to submit review:', err);
    }
    setReviewLoading(false);
  };

  if (!product) return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin"></div>
    </div>
  );

  const name = getLocalizedField(product, 'name', lang, product.name);
  const desc = getLocalizedField(product, 'description', lang, product.description);

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 page-enter">
      <Link to="/store" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors text-sm mb-8" data-testid="back-to-store">
        <ArrowLeft size={16} />{text('Volver a la tienda', 'Back to store')}
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        {/* Image */}
        <div className="aspect-square rounded-2xl overflow-hidden bg-card border border-border">
          <img src={product.images?.[0] || 'https://via.placeholder.com/600'} alt={name} className="w-full h-full object-cover" />
        </div>

        {/* Details */}
        <div className="flex flex-col gap-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <span className={`px-3 py-1 rounded-full text-xs font-semibold ${product.category === 'tcg' ? 'bg-[#8B5CF6]/20 text-[#A78BFA]' : product.category === 'board_game' ? 'bg-[#10B981]/20 text-[#34D399]' : 'bg-[#A1A1AA]/20 text-[#A1A1AA]'}`}>
                {product.category === 'board_game' ? text('Juego de Mesa', 'Board Game') : product.category === 'tcg' ? 'TCG' : text('Accesorio', 'Accessory')}
              </span>
              {product.is_rentable && <span className="px-3 py-1 rounded-full text-xs font-semibold bg-[#06B6D4]/20 text-[#06B6D4]">{text('Disponible para alquiler', 'Available to rent')}</span>}
            </div>
            <h1 className="text-3xl font-black text-foreground mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{name}</h1>
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => <Star key={`detail-star-${i}`} size={16} className={i < Math.round(product.rating) ? 'text-[#F59E0B] fill-[#F59E0B]' : 'text-[#A1A1AA]'} />)}
              <span className="text-muted-foreground text-sm">{product.rating?.toFixed(1)} ({product.review_count || 0} {text('reseñas', 'reviews')})</span>
            </div>
          </div>
          <p className="text-muted-foreground leading-relaxed">{desc}</p>

          {/* Specs */}
          {product.min_players && (
            <div className="flex gap-4">
              <div className="bg-card border border-border rounded-xl p-4 flex-1 text-center">
                <Users size={20} className="text-[#10B981] mx-auto mb-1" />
                <p className="text-foreground font-semibold text-sm">{product.min_players}-{product.max_players}</p>
                <p className="text-muted-foreground text-xs">{text('Jugadores', 'Players')}</p>
              </div>
              {product.duration_minutes && (
                <div className="bg-card border border-border rounded-xl p-4 flex-1 text-center">
                  <Clock size={20} className="text-[#8B5CF6] mx-auto mb-1" />
                  <p className="text-foreground font-semibold text-sm">{product.duration_minutes}</p>
                  <p className="text-muted-foreground text-xs">{text('Minutos', 'Minutes')}</p>
                </div>
              )}
              <div className="bg-card border border-border rounded-xl p-4 flex-1 text-center">
                <ShoppingCart size={20} className="text-[#06B6D4] mx-auto mb-1" />
                <p className="text-foreground font-semibold text-sm">{product.stock}</p>
                <p className="text-muted-foreground text-xs">{text('En stock', 'In stock')}</p>
              </div>
            </div>
          )}

          {/* Tags */}
          {product.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {product.tags.map(tag => <span key={tag} className="px-3 py-1 bg-muted/50 border border-border rounded-full text-xs text-muted-foreground">{tag}</span>)}
            </div>
          )}

          {/* Price & Buy */}
          <div className="bg-card border border-border rounded-2xl p-6">
            {product.is_pre_order ? (
              /* Pre-order product */
              <div>
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-[#C9A962]/20 text-[#C9A962] rounded-full text-xs font-semibold">
                    {text('Sólo por reserva previa', 'Pre-order only')}
                  </span>
                </div>
                <span className="text-4xl font-black text-[#10B981] block mb-4" data-testid="product-price">{product.price?.toFixed(2)}€</span>
                {reserveMsg ? (
                  <p className={`text-sm font-medium py-3 px-4 rounded-xl ${reserveMsg.includes('Error') || reserveMsg.includes('error') ? 'bg-red-500/10 text-red-400' : 'bg-[#10B981]/10 text-[#10B981]'}`}>{reserveMsg}</p>
                ) : (
                  <button
                    onClick={handlePreOrder}
                    disabled={reserving}
                    className="w-full py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-[#C9A962]/20 text-[#C9A962] hover:bg-[#C9A962]/30 border border-[#C9A962]/30 transition-all disabled:opacity-60"
                    data-testid="pre-order-btn"
                  >
                    <Bell size={16} />
                    {reserving ? '...' : text('Reservar / Pre-orden', 'Pre-order / Reserve')}
                  </button>
                )}
              </div>
            ) : (
              /* Normal product */
              <>
                <div className="flex items-center justify-between mb-4">
                  <span className="text-4xl font-black text-[#10B981]" data-testid="product-price">{product.price?.toFixed(2)}€</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => setQuantity(Math.max(1, quantity - 1))} className="w-8 h-8 rounded-lg bg-[#1A1A24] flex items-center justify-center hover:bg-[#10B981]/20 transition-colors" data-testid="qty-minus"><Minus size={14} /></button>
                    <span className="text-foreground font-bold w-6 text-center" data-testid="quantity">{quantity}</span>
                    <button onClick={() => setQuantity(Math.min(product.stock, quantity + 1))} className="w-8 h-8 rounded-lg bg-[#1A1A24] flex items-center justify-center hover:bg-[#10B981]/20 transition-colors" data-testid="qty-plus"><Plus size={14} /></button>
                  </div>
                </div>
                {product.stock === 0 && (
                  <p className="text-sm text-[#C45B4A] mb-3 font-semibold">{text('Fuera de stock', 'Out of stock')}</p>
                )}
                <div className="flex gap-3 flex-col">
                  <button onClick={handleAddCart} disabled={product.stock === 0}
                    className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all ${added ? 'bg-[#10B981] text-[#0A0A0E]' : 'btn-primary'} disabled:opacity-50`}
                    data-testid="add-to-cart-btn">
                    <ShoppingCart size={16} />
                    {added ? text('¡Añadido!', 'Added!') : text('Añadir al Carrito', 'Add to Cart')}
                  </button>
                  {product.is_rentable && (
                    <Link to="/reservations/games" className="flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 bg-[#8B5CF6]/20 text-[#A78BFA] hover:bg-[#8B5CF6]/30 transition-colors border border-[#8B5CF6]/20" data-testid="reserve-btn">
                      <BookOpen size={16} />{text('Reservar', 'Reserve')}
                    </Link>
                  )}
                  {product.stock === 0 && (
                    <button
                      onClick={handleToggleNotify}
                      disabled={notifyLoading}
                      className={`flex-1 py-3 rounded-xl font-bold text-sm flex items-center justify-center gap-2 transition-all border ${
                        notified
                          ? 'bg-[#C9A962]/20 text-[#C9A962] border-[#C9A962]/30 hover:bg-[#EF4444]/10 hover:text-red-400 hover:border-red-400/30'
                          : 'bg-muted/50 text-muted-foreground border-border hover:border-primary/30 hover:text-primary'
                      }`}
                      data-testid="notify-btn"
                    >
                      <Bell size={16} />
                      {notifyLoading ? '...' : notified
                        ? text('✓ Avísame cuando esté', '✓ Notify me')
                        : text('Avísame cuando esté disponible', 'Notify me when available')}
                    </button>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Reviews */}
      <div className="mt-16" id="product-review">
        <h2 className="text-2xl font-bold text-foreground mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>{text('Reseñas', 'Reviews')}</h2>
        {user && (
          <form onSubmit={handleReview} className="bg-card border border-border rounded-2xl p-6 mb-8">
            <h3 className="text-foreground font-semibold mb-4">{text('Escribe una reseña', 'Write a review')}</h3>
            <div className="flex gap-2 mb-4">
              {[1,2,3,4,5].map(r => (
                <button type="button" key={r} onClick={() => setReviewForm(f => ({...f, rating: r}))} className="transition-transform hover:scale-110">
                  <Star size={24} className={r <= reviewForm.rating ? 'text-[#F59E0B] fill-[#F59E0B]' : 'text-[#A1A1AA]'} />
                </button>
              ))}
            </div>
            <textarea value={reviewForm.comment} onChange={e => setReviewForm(f => ({...f, comment: e.target.value}))} required
              placeholder={text('Comparte tu opinión...', 'Share your thoughts...')}
              className="w-full bg-[#0A0A0E] border border-white/10 rounded-xl px-4 py-3 text-white text-sm outline-none focus:border-[#10B981] resize-none h-24 placeholder:text-[#A1A1AA]/50"
              data-testid="review-textarea" />
            <button type="submit" disabled={reviewLoading} className="mt-3 btn-primary px-6 py-2 text-sm" data-testid="submit-review">
              {reviewLoading ? '...' : text('Enviar', 'Submit')}
            </button>
          </form>
        )}
        <div className="space-y-4">
          {(product.reviews || []).slice().reverse().map((review, i) => (
            <div key={review.user_id || `review-${i}`} className="bg-card border border-border rounded-xl p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#10B981] to-[#8B5CF6] flex items-center justify-center text-xs font-bold text-white">{review.user_name?.[0]?.toUpperCase()}</div>
                  <span className="text-foreground font-semibold text-sm">{review.user_name}</span>
                </div>
                <div className="flex gap-0.5">{[...Array(review.rating)].map((_, j) => <Star key={`review-star-${i}-${j}`} size={12} className="text-[#F59E0B] fill-[#F59E0B]" />)}</div>
              </div>
              <p className="text-muted-foreground text-sm">{review.comment}</p>
            </div>
          ))}
          {(!product.reviews || product.reviews.length === 0) && (
            <p className="text-muted-foreground text-sm">{text('Sin reseñas aún.', 'No reviews yet.')}</p>
          )}
        </div>
      </div>
    </div>
  );
}
